import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { RefreshCw, ExternalLink, UserCheck } from "lucide-react";

/* Agency Production Cost Estimator (Phase 1, 2026-09) — staff-side review inbox for
 * agency_quote_requests. "Open in Quote Builder" is the ONLY path that ever turns one of
 * these into a real quote_drafts row (POST .../convert-to-quote) — never automatic.
 *
 * PM Feedback Batch 1, item 1 (2026-09): "Pick up" (informational only — anyone can take
 * over), Status flow (Submitted -> In review -> Quote sent | Call requested | Declined |
 * Closed), and an optional note to the agency (surfaced read-only on their "My requests"
 * view — never emailed to the agency). */
const STATUSES = ["Submitted", "In review", "Quote sent", "Call requested", "Declined", "Closed"];
const STATUS_STYLES = {
  "Submitted": "bg-[#FFC817]/15 text-[#8A6A00]",
  "In review": "bg-blue-50 text-blue-700",
  "Quote sent": "bg-emerald-50 text-emerald-700",
  "Call requested": "bg-purple-50 text-purple-700",
  "Declined": "bg-red-50 text-red-700",
  "Closed": "bg-[#F5F5F4] text-[#78716C]",
};

export default function AgencyQuoteRequests() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [converting, setConverting] = useState(null);
  const [pickingUp, setPickingUp] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingStatus, setSavingStatus] = useState(null);

  const load = (status) => {
    setLoading(true);
    api.get("/v1/build/agency-quote-requests", { params: status ? { status } : {} })
      .then((r) => { setItems(r.data.items || []); setAccessDenied(false); })
      // Bugfix (2026-09-29, Bavna's live regression): a 403 here used to fail silently
      // (no .catch at all) and just render an empty "No requests yet" list — indistinguishable
      // from genuinely having zero requests. Never a logout (this app's axios interceptor only
      // ever treats 401 that way), but still confusing. Now shows an explicit, friendly message.
      .catch((e) => { if (e?.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(""); }, []);

  const convert = async (id) => {
    setConverting(id);
    try {
      const r = await api.post(`/v1/build/agency-quote-requests/${id}/convert-to-quote`);
      toast.success("Opened in Quote Builder");
      nav(`/commercial?draft_id=${r.data.draft_id}`);
    } catch { toast.error("Couldn't convert this request"); }
    finally { setConverting(null); }
  };

  const pickup = async (id) => {
    setPickingUp(id);
    try {
      const r = await api.post(`/v1/build/agency-quote-requests/${id}/pickup`);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...r.data.item } : it)));
      toast.success("Picked up");
    } catch { toast.error("Couldn't pick up this request"); }
    finally { setPickingUp(null); }
  };

  const changeStatus = async (id, status) => {
    setSavingStatus(id);
    try {
      const r = await api.patch(`/v1/build/agency-quote-requests/${id}/status`, { status, note: noteDrafts[id] || "" });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...r.data.item } : it)));
      setNoteDrafts((prev) => ({ ...prev, [id]: "" }));
      toast.success(`Status set to "${status}"`);
    } catch { toast.error("Couldn't update status"); }
    finally { setSavingStatus(null); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="agency-quote-requests-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-editorial text-4xl tracking-tight">Agency Quote Requests</h1>
          <p className="text-sm text-[#57534E] mt-1">Production cost estimates submitted by Partner Agencies through the Agency Portal.</p>
        </div>
        <button onClick={() => load(statusFilter)} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:border-[#D6D3D1] flex items-center gap-1.5" data-testid="agency-quote-requests-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="agency-quote-requests-status-filter">
        {["", ...STATUSES].map((s) => (
          <button
            key={s || "all"}
            onClick={() => { setStatusFilter(s); load(s); }}
            data-testid={`agency-quote-requests-filter-${s ? s.replace(/\s+/g, "-").toLowerCase() : "all"}`}
            className={`rounded-full px-3 py-1 text-xs font-medium border ${statusFilter === s ? "bg-[#1C1C1E] text-white border-[#1C1C1E]" : "border-[#E7E5E4] text-[#57534E] hover:border-[#D6D3D1]"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#A8A29E]">Loading…</div>
      ) : accessDenied ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center text-sm text-[#57534E]" data-testid="agency-quote-requests-access-denied">
          You don't have access to Agency Requests. Ask a founder, partner or PM to grant access if you need it.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-8 text-center text-sm text-[#A8A29E]" data-testid="agency-quote-requests-empty">No requests yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} data-testid={`agency-quote-request-row-${r.id}`} className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1C1C1E]">{r.agency_name || "Unknown agency"}</span>
                    <span className="text-xs text-[#A8A29E]">— {r.production_type}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${STATUS_STYLES[r.status] || "bg-[#F5F5F4] text-[#78716C]"}`} data-testid={`agency-quote-request-status-${r.id}`}>{r.status}</span>
                  </div>
                  <div className="text-xs text-[#57534E] mt-2 flex flex-wrap gap-3">
                    {r.working_title && <span>{r.working_title}</span>}
                    {r.shoot_location && <span>{r.shoot_location}</span>}
                    {r.shoot_days && <span>{r.shoot_days} shoot day{r.shoot_days > 1 ? "s" : ""}</span>}
                    <span>Submitted by {r.submitted_by}</span>
                    {r.picked_up_by && <span data-testid={`agency-quote-request-picked-up-${r.id}`}>Picked up by {r.picked_up_by}</span>}
                  </div>
                  {r.brief && <p className="text-xs text-[#1C1C1E] mt-2 italic">"{r.brief}"</p>}
                  {r.staff_note && <p className="text-xs text-[#57534E] mt-2 bg-[#F5F5F4] rounded-md px-2.5 py-1.5" data-testid={`agency-quote-request-staff-note-${r.id}`}>Note to agency: {r.staff_note}</p>}
                  {r.reference_videos?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid={`agency-quote-request-reference-videos-${r.id}`}>
                      {r.reference_videos.map((v) => (
                        <div key={v.id} title={v.title} className="w-16 h-10 rounded overflow-hidden bg-[#F5F5F4] flex-shrink-0">
                          {v.thumbnail_url && <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0 space-y-2 w-full sm:w-auto">
                  <div className="text-sm font-semibold text-[#1C1C1E]" data-testid={`agency-quote-request-total-${r.id}`}>
                    {r.grand_total != null ? formatMoney(r.grand_total, r.currency) : "Price TBC"}
                  </div>
                  {r.quote_draft_id ? (
                    <button onClick={() => nav(`/commercial?draft_id=${r.quote_draft_id}`)} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:border-[#D6D3D1] inline-flex items-center gap-1 w-full justify-center" data-testid={`agency-quote-request-open-draft-${r.id}`}>
                      <ExternalLink className="h-3 w-3" /> Open Draft
                    </button>
                  ) : (
                    <button onClick={() => convert(r.id)} disabled={converting === r.id} className="rounded-md bg-[#1C1C1E] text-white px-3 py-1.5 text-xs font-medium hover:opacity-85 disabled:opacity-50 w-full" data-testid={`agency-quote-request-convert-${r.id}`}>
                      {converting === r.id ? "Opening…" : "Open in Quote Builder"}
                    </button>
                  )}
                  <button onClick={() => pickup(r.id)} disabled={pickingUp === r.id} className="rounded-md border border-[#E7E5E4] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:border-[#D6D3D1] inline-flex items-center gap-1 w-full justify-center disabled:opacity-50" data-testid={`agency-quote-request-pickup-${r.id}`}>
                    <UserCheck className="h-3 w-3" /> {pickingUp === r.id ? "Picking up…" : "Pick up"}
                  </button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#F5F5F4] flex flex-wrap items-center gap-2">
                <select
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) { changeStatus(r.id, e.target.value); e.target.value = ""; } }}
                  disabled={savingStatus === r.id}
                  data-testid={`agency-quote-request-status-select-${r.id}`}
                  className="rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2 py-1.5 text-xs text-[#1C1C1E]"
                >
                  <option value="">Change status…</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input
                  type="text" placeholder="Optional note to agency…" value={noteDrafts[r.id] || ""}
                  onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                  data-testid={`agency-quote-request-note-input-${r.id}`}
                  className="flex-1 min-w-[220px] rounded-md border border-[#E7E5E4] bg-[#FFFFFF] px-2.5 py-1.5 text-xs text-[#1C1C1E] focus:outline-none focus:border-[#FFC817]/50"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
